/**
 * Shared helpers for order-detail-v1 Vue components (ported from Lovable wireframe).
 */

export function formatDate(value, locale = 'it-IT') {
    if (!value) {
        return '—';
    }

    try {
        return new Intl.DateTimeFormat(locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(value));
    } catch (e) {
        return String(value);
    }
}

export function statusLabel(status) {
    const labels = {
        completed: 'Completato',
        succeeded: 'Completato',
        failed: 'Fallito',
        not_persisted: 'Non persistito',
        not_reached: 'Non raggiunto',
        processing: 'In corso',
        info: 'Info',
        mapped: 'Mappato',
        pending: 'In attesa',
    };

    return labels[status] || status;
}

export function statusBadgeClass(status) {
    if (status === 'completed' || status === 'succeeded' || status === 'mapped') {
        return 'label label-success';
    }
    if (status === 'failed') {
        return 'label label-danger';
    }
    if (status === 'not_persisted' || status === 'not_reached') {
        return 'label label-default order-detail-v1__badge--dashed';
    }
    if (status === 'pending' || status === 'processing') {
        return 'label label-warning';
    }
    if (status === 'info') {
        return 'label label-info';
    }

    return 'label label-default';
}

const STATUS_BADGES = {
    succeeded: { label: 'Completato', tone: 'success' },
    completed: { label: 'Completato', tone: 'success' },
    synced: { label: 'Completato', tone: 'success' },
    reserved: { label: 'Completato', tone: 'success' },
    mapped: { label: 'Completato', tone: 'success' },
    shipped: { label: 'Completato', tone: 'success' },
    running: { label: 'In corso', tone: 'progress' },
    in_progress: { label: 'In corso', tone: 'progress' },
    syncing: { label: 'In corso', tone: 'progress' },
    processing: { label: 'In corso', tone: 'progress' },
    pending: { label: 'In attesa', tone: 'waiting' },
    queued: { label: 'In attesa', tone: 'waiting' },
    failed: { label: 'Errore', tone: 'error' },
    error: { label: 'Errore', tone: 'error' },
    not_persisted: { label: 'Non persistito', tone: 'missing' },
    not_reached: { label: 'Non persistito', tone: 'missing' },
    info: { label: 'Info', tone: 'info' },
};

/**
 * Fixed badge vocabulary for the live panel; unknown raw values stay neutral.
 *
 * @returns {{ label: string, tone: string }}
 */
export function statusBadge(status) {
    return STATUS_BADGES[status] || { label: 'Sconosciuto', tone: 'unknown' };
}

const EVENT_LABELS = {
    'order.persisted': 'Ordine registrato',
    'order.syncing_to_beestore': 'Invio a BeeStore',
    'order.synced_to_beestore': 'Sincronizzato con BeeStore',
    'order.shipped': 'Spedito',
    'order.cancelled': 'Annullato',
    'order.archived': 'Archiviato',
    'order.refunded': 'Rimborsato',
    'return.requested': 'Reso richiesto',
    'return.accepted': 'Reso accettato',
    'return.refused': 'Reso rifiutato',
    'return.archived': 'Reso archiviato',
    'Shopify sync task': 'Sincronizzazione Shopify',
};

/**
 * Italian sentence-case label for hop titles and lifecycle event keys; never a raw dotted key.
 */
export function eventLabel(raw) {
    if (!raw) {
        return '—';
    }
    if (EVENT_LABELS[raw]) {
        return EVENT_LABELS[raw];
    }
    if (!/^[\w-]+(\.[\w-]+)+$/.test(raw)) {
        return raw;
    }
    const last = raw.split('.').pop().replace(/[_-]+/g, ' ');

    return last.charAt(0).toUpperCase() + last.slice(1);
}

/**
 * Parses "k=v · k=v" hop detail strings into [key, value] pairs.
 *
 * @returns {Array<[string, string]>}
 */
export function parseTechPairs(raw, fallbackKey = 'Riferimento') {
    if (!raw) {
        return [];
    }

    return String(raw).split(' · ').filter(Boolean).map((part) => {
        const i = part.indexOf('=');

        return i > 0 ? [part.slice(0, i).trim(), part.slice(i + 1).trim()] : [fallbackKey, part.trim()];
    });
}
